import { Document, BLOCKS, INLINES } from '@contentful/rich-text-types';
import Typography from '@mui/material/Typography';
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';

import {
  expandTextMacros,
  containsDateMacros,
} from 'lib/text-macros/textMacroHelper';

// Note: The renderText method will change all newlines and change them into br tags.
// If that is not done, react will choke on some browsers as the newline will cause a
// plethora of hydration errors as some browser will filter them out and then you have
// a gross mismatch of content.
const defaultOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => (
      <Typography variant="h1" component="h1" suppressHydrationWarning>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Typography variant="h2" component="h2" suppressHydrationWarning>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Typography variant="h3" component="h3" suppressHydrationWarning>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <Typography variant="h4" component="h4" suppressHydrationWarning>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_5]: (_, children) => (
      <Typography variant="h5" component="h5" suppressHydrationWarning>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_6]: (_, children) => (
      <Typography variant="h6" component="h6" suppressHydrationWarning>
        {children}
      </Typography>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Typography
        variant="body1"
        component="p"
        sx={{
          marginBottom: 2,
        }}
      >
        {children}
      </Typography>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const isExternalLink =
        node.data.uri.startsWith('http') && !node.data.uri.includes('smava.de');
      if (isExternalLink) {
        return (
          <a href={node.data.uri} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      }

      return <a href={node.data.uri}>{children}</a>;
    },
  },

  renderText: (text) => {
    // Handle empty or whitespace-only text
    if (!text || text.trim() === '') {
      return text;
    }

    const segments = text.split('\n');

    // If there's no newlines, just process the text normally
    if (segments.length === 1) {
      const expandedText = expandTextMacros(text);
      const hasMacros = containsDateMacros(text);

      return hasMacros ? (
        <span suppressHydrationWarning>{expandedText}</span>
      ) : (
        expandedText
      );
    }

    // Handle multiple segments with newlines
    return (
      <>
        {segments.map((textSegment, index) => {
          const expandedText = expandTextMacros(textSegment);
          const hasMacros = containsDateMacros(textSegment);

          return (
            <>
              {index > 0 && <br />}
              {hasMacros ? (
                <span suppressHydrationWarning>{expandedText}</span>
              ) : (
                expandedText
              )}
            </>
          );
        })}
      </>
    );
  },
};

interface RichTextProps {
  document?: Document;
  options?: Options;
}

export function RichText(props: RichTextProps) {
  const { document, options } = props;
  if (!document) return <></>;

  const mergedOptions = {
    renderNode: { ...defaultOptions.renderNode, ...options?.renderNode },
    renderText: options?.renderText ?? defaultOptions.renderText,
  };

  const reactComponents = documentToReactComponents(document, mergedOptions);
  return <>{reactComponents}</>;
}
